import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { trackEvent, scrollToId } from "@/lib/analytics";

const navLinks = [
  { label: "How It Works", id: "how-it-works" },
  { label: "Experiences", id: "featured" },
  { label: "Travel List", id: "lead-capture" },
  { label: "FAQ", id: "faq" },
];

export const Navbar = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  const goLogin = () => {
    setOpen(false);
    navigate("/admin/login");
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-sand/90 backdrop-blur-md border-b border-bone shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => scrollToId("hero")}
          className={`font-serif text-2xl tracking-tight font-semibold ${
            scrolled ? "text-ink" : "text-white"
          }`}
        >
          KIS Living<span className="text-terracotta">.</span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {navLinks.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className={`text-sm font-medium transition-colors hover:text-terracotta ${
                scrolled ? "text-ink-soft" : "text-white/90"
              }`}
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-login-link"
            onClick={goLogin}
            className={`text-sm font-medium transition-colors hover:text-terracotta ${
              scrolled ? "text-ink-soft" : "text-white/90"
            }`}
          >
            Login
          </button>
          <Button
            data-testid="nav-join-button"
            onClick={() => {
              trackEvent("cta_click", { location: "navbar", cta: "join_travel_list" });
              go("lead-capture");
            }}
            className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-6"
          >
            Join the Travel List
          </Button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className={`md:hidden ${scrolled ? "text-ink" : "text-white"}`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden bg-sand border-t border-bone px-6 py-6 flex flex-col gap-4">
          {navLinks.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-left text-ink-soft font-medium py-1"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-mobile-login-link"
            onClick={goLogin}
            className="text-left text-ink-soft font-medium py-1"
          >
            Login
          </button>
          <Button
            data-testid="nav-mobile-join-button"
            onClick={() => go("lead-capture")}
            className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white w-full"
          >
            Join the Travel List
          </Button>
        </div>
      )}
    </header>
  );
};
